import React, { useEffect, useImperativeHandle, useState } from "react";

type InputProps = Omit<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
  "placeholder"
> & {
  placeholder?: string;
  errors?: Record<string, unknown>;
  touched?: Record<string, unknown>;
  name: string;
  iconSrc?: string;
  topLabel?: string;
};

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    { type, name, placeholder, touched, required, topLabel, iconSrc, ...props },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [showPassword, setShowPassword] = useState(false);
    const [inputType, setInputType] = useState(type);

    useEffect(() => {
      if (type === "password") {
        setInputType(showPassword ? "text" : "password");
      }
    }, [type, showPassword]);

    useImperativeHandle(ref, () => inputRef.current!);

    return (
      <div className="flex flex-col w-full mb-[2.3rem]">
        {topLabel && (
          <label className="mb-[0.4rem] flex items-center space-x-1">
            <p>{topLabel}</p>
            {required && <span className="text-rose-500">*</span>}
          </label>
        )}
        <div className="relative z-0 w-full border rounded-[0.5rem]">
          <input
            type={inputType}
            name={name}
            placeholder={placeholder}
            required={required}
            className="p-[1rem] block w-full h-[5rem] mt-0 border rounded-[0.5rem] appearance-none focus:outline-none focus:ring-0 text-[1.6rem]"
            {...props}
            ref={inputRef}
          />
          {iconSrc && (
            <img
              src={iconSrc}
              width={20}
              height={20}
              className="absolute left-[1rem] top-1/2 transform -translate-y-1/2"
              alt="icon"
            />
          )}
          {type === "password" && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-[1rem] top-1/2 transform -translate-y-1/2 text-gray-600"
            >
              {showPassword ? <p>Hide</p> : <p>Show</p>}
            </button>
          )}
        </div>
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
